/**
 * AgenticED Common UI Components
 * 
 * Shared presentational components for badges, cards, stats, etc.
 * Used across Vendor Portal and District Admin dashboards.
 * 
 * @version 1.0.0
 */

import React from 'react';

// ============================================================================
// STATUS BADGE
// ============================================================================

const STATUS_VARIANTS = {
    // Certification status
    draft: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    submitted: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
    testing: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
    review: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
    provisional: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
    certified: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
    suspended: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },

    // General status
    active: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
    inactive: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
    enabled: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
    disabled: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },

    // Test results
    passed: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
    warning: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
    skipped: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },

    // Consent status
    granted: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
    revoked: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
    expired: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },

    // User roles
    admin: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
    developer: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
    viewer: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },

    // Multi-word statuses
    in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
    in_review: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
    not_configured: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    onboarding: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' }
};

function formatDisplayName(value) {
    if (!value) return '';
    // Safety net: callers are expected to pass strings, but JSONB-backed
    // columns occasionally surface as object/array shapes when an API
    // forgets to project a leaf field (e.g. agent_registry.capabilities
    // is an array of {name, description, ...} objects, not strings).
    // Without this guard, .replace below throws "x.replace is not a
    // function" and takes the whole panel down. We prefer obviously-broken
    // text to a runtime crash, and warn so the caller-side fix gets done.
    if (typeof value !== 'string') {
        if (typeof console !== 'undefined' && typeof console.warn === 'function') {
            console.warn('[formatDisplayName] non-string input — caller should project a string field', {
                type: typeof value,
                preview: (() => {
                    try { return JSON.stringify(value).slice(0, 120); }
                    catch (_e) { return '[unserializable]'; }
                })(),
            });
        }
        const fallback = value && typeof value === 'object'
            ? (value.name ?? value.label ?? value.id ?? '')
            : '';
        if (typeof fallback !== 'string' || fallback === '') return '';
        value = fallback;
    }
    return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function StatusBadge({ status, showDot = true, label = null, className = '' }) {
    const variant = STATUS_VARIANTS[status?.toLowerCase()] || STATUS_VARIANTS.draft;
    const displayLabel = label || formatDisplayName(status);

    return (
        <span className={`
            inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium
            ${variant.bg} ${variant.text} ${className}
        `}>
            {showDot && (
                <span className={`w-1.5 h-1.5 rounded-full ${variant.dot}`} />
            )}
            {displayLabel}
        </span>
    );
}

// ============================================================================
// STAT CARD
// ============================================================================

function StatCard({ label, value, change = null, changeType = null, icon = null, className = '' }) {
    const isPositive = changeType === 'positive' || (change && change > 0);
    const isNegative = changeType === 'negative' || (change && change < 0);

    return (
        <div className={`bg-white rounded-lg border border-slate-200 p-5 ${className}`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-slate-500 mb-1">{label}</p>
                    <p className="text-2xl font-semibold text-slate-900">{typeof value === 'number' ? value.toLocaleString('en-US') : value}</p>
                    {change !== undefined && (
                        <p className={`text-xs mt-1 flex items-center gap-1 ${
                            isPositive ? 'text-green-600' : 
                            isNegative ? 'text-red-600' : 
                            'text-slate-500'
                        }`}>
                            {isPositive && (
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 4l-8 8h5v8h6v-8h5z" />
                                </svg>
                            )}
                            {isNegative && (
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 20l8-8h-5V4H9v8H4z" />
                                </svg>
                            )}
                            {typeof change === 'number' ? `${Math.abs(change)}%` : change}
                        </p>
                    )}
                </div>
                {icon && (
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                        {icon}
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// CARD
// ============================================================================

function Card({ title = null, subtitle = null, actions = null, children, className = '', noPadding = false }) {
    return (
        <div className={`bg-white rounded-lg border border-slate-200 ${className}`}>
            {(title || subtitle || actions) && (
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        {title && (
                            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
                        )}
                        {subtitle && (
                            <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
                        )}
                    </div>
                    {actions && (
                        <div className="flex items-center gap-2">
                            {actions}
                        </div>
                    )}
                </div>
            )}
            <div className={noPadding ? '' : 'p-5'}>
                {children}
            </div>
        </div>
    );
}

// ============================================================================
// BUTTON
// ============================================================================

const BUTTON_VARIANTS = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:ring-slate-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 focus:ring-slate-500'
};

const BUTTON_SIZES = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base'
};

function Button({ 
    variant = 'primary', 
    size = 'md', 
    loading = false, 
    disabled = false,
    icon = null,
    children, 
    className = '', 
    ...props 
}) {
    return (
        <button
            disabled={disabled || loading}
            className={`
                inline-flex items-center justify-center gap-2 rounded-lg font-medium
                transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${BUTTON_VARIANTS[variant]}
                ${BUTTON_SIZES[size]}
                ${className}
            `}
            {...props}
        >
            {loading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
            ) : icon}
            {children}
        </button>
    );
}

// ============================================================================
// INPUT
// ============================================================================

function Input({ 
    label = null, 
    error = null, 
    helper = null, 
    icon = null,
    className = '', 
    ...props 
}) {
    return (
        <div className={className}>
            {label && (
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    {label}
                </label>
            )}
            <div className="relative">
                {icon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        {icon}
                    </div>
                )}
                <input
                    className={`
                        w-full px-3 py-2 border rounded-lg text-sm
                        placeholder:text-slate-400
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        disabled:bg-slate-50 disabled:cursor-not-allowed
                        ${icon ? 'pl-10' : ''}
                        ${error 
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                            : 'border-slate-300'
                        }
                    `}
                    {...props}
                />
            </div>
            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
            {helper && !error && (
                <p className="mt-1 text-sm text-slate-500">{helper}</p>
            )}
        </div>
    );
}

// ============================================================================
// SELECT
// ============================================================================

function Select({ label = null, error = null, options = [], placeholder = '', className = '', ...props }) {
    return (
        <div className={className}>
            {label && (
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    {label}
                </label>
            )}
            <select
                className={`
                    w-full px-3 py-2 border rounded-lg text-sm bg-white
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    disabled:bg-slate-50 disabled:cursor-not-allowed
                    ${error 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-slate-300'
                    }
                `}
                {...props}
            >
                {placeholder && (
                    <option value="">{placeholder}</option>
                )}
                {options.map((option) => (
                    <option 
                        key={option.value} 
                        value={option.value}
                        disabled={option.disabled}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
}

// ============================================================================
// MODAL
// ============================================================================

function Modal({ isOpen, onClose, title, children, footer = null, size = 'md' }) {
    if (!isOpen) return null;

    const sizes = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl'
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                {/* Backdrop */}
                <div 
                    className="fixed inset-0 bg-black/50 transition-opacity"
                    onClick={onClose}
                />

                {/* Modal */}
                <div className={`
                    relative bg-white rounded-xl shadow-xl w-full
                    transform transition-all
                    ${sizes[size]}
                `}>
                    {/* Header */}
                    {title && (
                        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
                            <button
                                onClick={onClose}
                                className="p-1 text-slate-400 hover:text-slate-600 rounded"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* Body */}
                    <div className="px-6 py-4">
                        {children}
                    </div>

                    {/* Footer */}
                    {footer && (
                        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// ALERT
// ============================================================================

const ALERT_VARIANTS = {
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'text-blue-500' },
    success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: 'text-green-500' },
    warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: 'text-yellow-500' },
    error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-500' }
};

function Alert({ variant = 'info', title = null, children, onDismiss = null, className = '' }) {
    const styles = ALERT_VARIANTS[variant];

    return (
        <div className={`
            rounded-lg border p-4
            ${styles.bg} ${styles.border} ${className}
        `}>
            <div className="flex gap-3">
                <div className={`flex-shrink-0 ${styles.icon}`}>
                    {variant === 'info' && (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                    )}
                    {variant === 'success' && (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    )}
                    {variant === 'warning' && (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    )}
                    {variant === 'error' && (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                    )}
                </div>
                <div className="flex-1">
                    {title && (
                        <h4 className={`text-sm font-medium ${styles.text}`}>{title}</h4>
                    )}
                    <div className={`text-sm ${styles.text} ${title ? 'mt-1' : ''}`}>
                        {children}
                    </div>
                </div>
                {onDismiss && (
                    <button
                        onClick={onDismiss}
                        className={`flex-shrink-0 ${styles.icon} hover:opacity-70`}
                    >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// TABS
// ============================================================================

function Tabs({ tabs, activeTab, onChange, className = '' }) {
    return (
        <div className={`border-b border-slate-200 ${className}`}>
            <nav className="flex gap-6">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => onChange(tab.id)}
                        className={`
                            pb-3 text-sm font-medium border-b-2 -mb-px transition-colors
                            ${activeTab === tab.id
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                            }
                        `}
                    >
                        {tab.label}
                        {tab.count !== undefined && (
                            <span className={`
                                ml-2 px-2 py-0.5 rounded-full text-xs
                                ${activeTab === tab.id
                                    ? 'bg-blue-100 text-blue-600'
                                    : 'bg-slate-100 text-slate-600'
                                }
                            `}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}
            </nav>
        </div>
    );
}

// ============================================================================
// PROGRESS BAR
// ============================================================================

function ProgressBar({ value, max = 100, label = null, showValue = true, variant = 'default', className = '' }) {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
    
    const variants = {
        default: 'bg-blue-600',
        success: 'bg-green-600',
        warning: 'bg-yellow-500',
        danger: 'bg-red-600'
    };

    const getVariant = () => {
        if (variant !== 'default') return variants[variant];
        if (percentage >= 90) return variants.danger;
        if (percentage >= 75) return variants.warning;
        return variants.default;
    };

    return (
        <div className={className}>
            {(label || showValue) && (
                <div className="flex justify-between mb-1">
                    {label && <span className="text-sm text-slate-600">{label}</span>}
                    {showValue && <span className="text-sm font-medium text-slate-900">{percentage.toFixed(0)}%</span>}
                </div>
            )}
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-300 ${getVariant()}`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
}

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

function EmptyState({ icon = null, title, description, action = null }) {
    return (
        <div className="text-center py-12">
            {icon && (
                <div className="mx-auto w-12 h-12 text-slate-300 mb-4">
                    {icon}
                </div>
            )}
            <h3 className="text-sm font-medium text-slate-900 mb-1">{title}</h3>
            {description && (
                <p className="text-sm text-slate-500 mb-4">{description}</p>
            )}
            {action}
        </div>
    );
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    formatDisplayName,
    StatusBadge,
    StatCard,
    Card,
    Button,
    Input,
    Select,
    Modal,
    Alert,
    Tabs,
    ProgressBar,
    EmptyState
};
